import { COUPON_TYPE_OPTIONS } from "../../../pages/private/Coupons/schema";

// Types for GET /v1/coupon/list_coupons
export type CouponType = (typeof COUPON_TYPE_OPTIONS)[number];

export type CouponStatus = "active" | "expired";

export const COUPON_STATUS_OPTIONS: {
  label: string;
  value: CouponStatus | null;
}[] = [
  { label: "All", value: null },
  { label: "Active", value: "active" },
  { label: "Expired", value: "expired" },
];

/**
 * Server-side filter for `list_coupons.discountType`.
 *  - "fixed"      → matches coupons that use `couponAmount` (₦)
 *  - "percentage" → matches coupons that use `couponDiscount` (%)
 * Lowercase wire values per the API spec.
 */
export type CouponDiscountType = "percentage" | "fixed";

export const COUPON_DISCOUNT_TYPE_OPTIONS: {
  label: string;
  value: CouponDiscountType | null;
}[] = [
  { label: "All", value: null },
  { label: "Amount (\u20a6)", value: "fixed" },
  { label: "Percentage (%)", value: "percentage" },
];

export interface ListCouponsQuery {
  page?: number;
  limit?: number;
  couponType?: CouponType | null;
  status?: CouponStatus | null;
  /** Broad fuzzy match on coupon code, buyer name, or buyer email. */
  search?: string;
  /** Narrow filter scoped to the assigned buyer's name or email. */
  buyerName?: string;
  discountType?: CouponDiscountType | null;
  createdFrom?: string;
  createdTo?: string;
  expiryFrom?: string;
  expiryTo?: string;
}

export interface AssignedUser {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  status: string;
  currentBalance: string;
  dateAssigned: string;
}

export interface CouponListItem {
  id: string;
  couponCode: string;
  couponType: CouponType | string;
  couponAmount: string | null;
  couponDiscount: string | null;
  expiryDate: string;
  cooldownPeriod: number;
  activeDuration: number;
  isSignupBonus: boolean;
  isFirstOrderDiscount: boolean;
  createdAt: string;
  assignedUsers: AssignedUser[];
}

export interface CouponListMeta {
  totalItems: number;
  itemCount: number;
  itemsPerPage: number;
  totalPages: number;
  currentPage: number;
}

export interface CouponListResponse {
  data: {
    items: CouponListItem[];
    meta: CouponListMeta;
  };
  message: string;
  isSuccessful: boolean;
}

// ---------------------------------------------------------------------------
// Types for GET /v1/coupon/used_coupons
//
// Each row is a redemption event, not a coupon — coupon details are nested
// under `coupon` and the redeeming buyer under `assignedTo`. `dateApplied` is
// the headline field (when the buyer actually used the coupon).
// ---------------------------------------------------------------------------

export interface UsedCouponAssignee {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
}

export interface UsedCouponSummary {
  id: string;
  couponCode: string;
  couponType: CouponType | string;
  couponAmount: string | null;
  couponDiscount: string | null;
  expiryDate: string;
}

export interface UsedCouponItem {
  id: string;
  assignedTo: UsedCouponAssignee;
  coupon: UsedCouponSummary;
  assignedAt: string;
  dateApplied: string;
}

export interface UsedCouponListResponse {
  data: {
    items: UsedCouponItem[];
    meta: CouponListMeta;
  };
  message: string;
  isSuccessful: boolean;
}
